import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/arrayUtils'
import React, { useMemo } from 'react'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import styled from 'styled-components'
import InlineGroup from 'components/Common/InlineGroup'
import SolidUsdCircleIcon from 'components/Luxkit/Icons/solid/SolidUsdCircleIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { getFlightDealIdsByYearMonth } from 'selectors/offerPageSelectors'
import { pluralizeToString } from 'lib/pluralize'

const RowBlock = styled(InlineGroup)`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const Root = styled(InlineGroup)`
  flex-direction: column;
  align-items: flex-start;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
  }
`

const FlightDealNameContainer = styled(BodyText)`
  display: flex;

  & > div {
    padding-right: ${rem(5)};
  }
`

interface Props {
  fromPrice: number;
  duration: number;
  withFlights?: boolean;
  rooms: App.RoomOccupants[];
  year?: string;
  month?: string;
  flightDealIdsByYearMonth?: Record<string, Record<string, string>>;
}

function OfferCalendarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    flightDealIdsByYearMonth,
    year,
    month,
    rooms = EmptyArray,
  } = props

  const averagePrice = Math.ceil(fromPrice / (rooms.length > 1 ? rooms.length : duration))

  const flightDealName = useMemo(() => {
    if (year && year in flightDealIdsByYearMonth) {
      const yearRecord = flightDealIdsByYearMonth[year]

      if (month) {
        if (month in yearRecord) {
          return yearRecord[month]
        }

        return undefined
      }

      return yearRecord[Object.keys(yearRecord)[0]]
    }

    return undefined
  }, [flightDealIdsByYearMonth, year, month])

  const perUnit = rooms.length > 1 ? 'room' : 'night'

  return (
    <Root vertical="center" horizontal="between">
      <RowBlock vertical="start">
        <BodyText variant="medium" colour="neutral-two">
          {rooms.length > 1 && pluralizeToString('room', rooms.length)}
          {pluralizeToString('night', duration)}{withFlights ? ' + ' + 'flights' : undefined}{' '}
          {withFlights && <>
            from{' '}
            <BodyText as="span" variant="medium" colour="neutral-one" weight="bold">
              <FormatCurrency value={fromPrice} />*
            </BodyText> total.
            {' '}
            Average <FormatCurrency value={averagePrice} />*/{ perUnit }
          </>}
          {!withFlights && <>
            from{' '}
            <BodyText as="span" variant="medium" colour="neutral-one" weight="bold">
              <FormatCurrency value={fromPrice} />
            </BodyText> total.
            {' '}
            Average <FormatCurrency value={averagePrice} />/{ perUnit }
          </>}
          {' '}
        </BodyText>
        {flightDealName && <FlightDealNameContainer variant="medium" colour="highlight">
          <div>
            <SolidUsdCircleIcon size="S" colour="secondary-normal" />
          </div>{ flightDealName }
        </FlightDealNameContainer>
        }
      </RowBlock>
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    flightDealIdsByYearMonth: getFlightDealIdsByYearMonth(appState, state).dealIds,
  }
})(OfferCalendarPriceBlurb)
